<script setup lang="ts">
import FormControl from '@fondof-ui/components/form/FormControl.vue'
import FormItem from '@satch-ui/primitives/form/FormItem.vue'
import Input from '@satch-ui/primitives/input/Input.vue'
import { localizeUrl } from '@shared/fondof-fn/url/localize'
import { useSearchStore } from '@shared/global-store/stores/search'
import type { Suggestions } from '@shared/global-store/stores/search/types'
import { getSearchSuggestions } from '@shared/shop-api/search'
import { t } from '@storefront-satch/lib/translations'
import { toTypedSchema } from '@vee-validate/zod'
import { useDebounceFn } from '@vueuse/core'
import clsx from 'clsx'
import { Field as FormField, useForm } from 'vee-validate'
import * as z from 'zod'

const props = defineProps<{
  class?: string | undefined
  query?: string | undefined
  id?: string | undefined
}>()

const searchStore = useSearchStore()

const schema = z.object({
  q: z.string().default(''),
})

const { isSubmitting, resetField, handleSubmit } = useForm({
  validationSchema: toTypedSchema(schema),
  initialValues: {
    q: props.query || '',
  },
})

const reset = () => {
  searchStore.$reset()
  resetField('q', { value: '' })
}

const onInput = useDebounceFn(async (event) => {
  const suggestions = (await getSearchSuggestions(
    event.target.value
  )) as Suggestions

  searchStore.setQuery(event.target.value)
  searchStore.setSuggestions(suggestions)
}, 250)

const onSubmit = handleSubmit((values) => {
  searchStore.isOpen = false
  globalThis.location.assign(
    localizeUrl('/search/products', {
      q: values.q,
      suggestion: 'false',
    })
  )
})
</script>

<template>
  <form
    :class="clsx('group isolate inline-flex w-full items-center', props.class)"
    @submit="onSubmit"
  >
    <FormField
      v-slot="{ componentField }"
      name="q"
    >
      <FormItem
        :class="
          clsx(
            'bg-gray-ultra-light has-[:focus]:bg-white lg:h-[52px]',
            'has-[:focus]:outline has-[:focus]:outline-2 has-[:focus]:outline-black',
            'hover:outline hover:outline-2 hover:outline-black',
            'grid grid-cols-[var(--spacing-10)_1fr_var(--spacing-10)]',
            '[&>[data-slot=icon]:first-child]:place-self-start [&>[data-slot=icon]:last-child]:place-self-end',
            '[&>[data-slot=icon]]:z-10 [&>[data-slot=icon]]:row-start-1',
            '[&>[data-slot=icon]:first-child]:col-start-1 [&>[data-slot=icon]:last-child]:col-start-3',
            '[&>[data-slot=control]]:col-span-3 [&>[data-slot=control]]:col-start-1 [&>[data-slot=control]]:row-start-1',
            '[&:has([data-slot=control]+[data-slot=icon])>[data-slot=control]]:pr-4 [&>[data-slot=icon]+[data-slot=control]]:pl-[61px]',
            '[&>[data-slot=icon]+[data-slot=control]]:md:pl-[72px]'
          )
        "
      >
        <slot
          name="button-before"
          :disabled="isSubmitting"
        />
        <FormControl>
          <Input
            :id="props.id"
            v-bind="componentField"
            class="border-none py-0 placeholder:text-black focus:bg-white focus:font-semibold lg:h-[52px]"
            type="search"
            size="small"
            :padding="null"
            :placeholder="t('navigation.search.placeholder')"
            autocomplete="off"
            @input="onInput"
          />
        </FormControl>
        <slot
          v-if="componentField?.modelValue.length"
          name="button-after"
          :handle-click="reset"
        />
      </FormItem>
    </FormField>
  </form>
</template>
